import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive, ArrowLeft, ChevronDown, ChevronRight, ChevronUp, FolderInput, FolderPlus, GripVertical,
  Loader2, Lock, Pencil, Plus, Search, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import { KbEditDialog } from "@/components/KbEditDialog";
import { KbIcon } from "@/components/KbIcon";
import { KbMoveCategoryDialog } from "@/components/KbMoveCategoryDialog";
import { KbTagFilter } from "@/components/KbTagFilter";
import { KB_AUDIENCES, audienceLabel } from "@/lib/kbAudiences";
import AppUnavailable from "@/pages/AppUnavailable";

// Order a flat category list into tree (DFS) with depth for indentation.
function ordered(cats) {
  const byParent = {};
  cats.forEach((c) => { (byParent[c.parent_id || "root"] ||= []).push(c); });
  Object.values(byParent).forEach((arr) => arr.sort((a, b) => (a.position || 0) - (b.position || 0)));
  const out = [];
  const walk = (pid, depth) => (byParent[pid] || []).forEach((c) => { out.push({ ...c, _depth: depth }); walk(c.cat_id, depth + 1); });
  walk("root", 0);
  return out;
}

export default function KBManageCategories() {
  const navigate = useNavigate();
  const [cats, setCats] = useState(null);
  const [denied, setDenied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [moving, setMoving] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", parent_id: "all", scope: "external" });
  const dragRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);   // { id, mode: before|after|inside }
  // Filters
  const [q, setQ] = usePersistedState("kb-categories.q", "");
  const [branches, setBranches] = usePersistedState("kb-categories.branches", []);
  const [vis, setVis] = usePersistedState("kb-categories.vis", "all");
  const [level, setLevel] = usePersistedState("kb-categories.level", "all");
  const [content, setContent] = usePersistedState("kb-categories.content", "all");
  const [narrowed, setNarrowed] = usePersistedState("kb-categories.narrowed", "all");
  const [auds, setAuds] = usePersistedState("kb-categories.auds", []);
  const [teams, setTeams] = usePersistedState("kb-categories.teams", []);
  const [teamOptions, setTeamOptions] = useState([]);
  const [collapsed, setCollapsed] = useState(new Set());
  const collapseInit = useRef(false);

  const load = useCallback(() => {
    api.get("/kb/manage/categories")
      .then((r) => {
        const list = r.data.categories || [];
        setCats(list);
        // Hundreds of rows at once is unusable, so branches start closed.
        if (!collapseInit.current) {
          collapseInit.current = true;
          setCollapsed(new Set(list.filter((c) => list.some((x) => x.parent_id === c.cat_id))
            .map((c) => c.cat_id)));
        }
      })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); else setCats([]); });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/kb/visibility/options")
      .then((r) => setTeamOptions(r.data.teams || []))
      .catch(() => {});
  }, []);

  const rows = useMemo(() => (cats ? ordered(cats) : []), [cats]);
  const childCount = useMemo(() => {
    const n = {};
    rows.forEach((c) => { if (c.parent_id) n[c.parent_id] = (n[c.parent_id] || 0) + 1; });
    return n;
  }, [rows]);
  const ancestry = useMemo(() => {
    const byId = Object.fromEntries(rows.map((c) => [c.cat_id, c]));
    const out = {};
    rows.forEach((c) => {
      const up = [];
      let p = byId[c.parent_id];
      let guard = 0;
      while (p && guard++ < 12) { up.push(p.cat_id); p = byId[p.parent_id]; }
      out[c.cat_id] = up;
    });
    return out;
  }, [rows]);

  const filtersOn = !!q.trim() || branches.length > 0 || auds.length > 0 || teams.length > 0
    || vis !== "all" || level !== "all" || content !== "all" || narrowed !== "all";

  const matched = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const hit = (c) => {
      if (needle && !`${c.name} ${c.slug}`.toLowerCase().includes(needle)) return false;
      if (branches.length && !branches.includes(c.cat_id)
        && !(ancestry[c.cat_id] || []).some((a) => branches.includes(a))) return false;
      if (vis !== "all" && (c.scope || "internal") !== vis) return false;
      // The API already falls back to the scope when nothing was saved, so a
      // legacy External category still answers to "Everyone".
      if (auds.length && !(c.audiences || []).some((a) => auds.includes(a))) return false;
      if (teams.length && !(c.target_teams || []).some((t) => teams.includes(t))) return false;
      if (level === "top" && c.parent_id) return false;
      if (level === "sub" && !c.parent_id) return false;
      if (content === "empty" && (c.article_count_deep || 0) > 0) return false;
      if (content === "has" && (c.article_count_deep || 0) === 0) return false;
      if (narrowed === "only"
        && !((c.target_teams || []).length || (c.target_orgs || []).length)) return false;
      return true;
    };
    return new Set(rows.filter(hit).map((c) => c.cat_id));
  }, [rows, q, branches, vis, level, content, narrowed, auds, teams, ancestry]);

  // Filtered: matches plus the folders above them, so a hit still reads in
  // context. Unfiltered: hide whatever sits under a closed branch.
  const shown = useMemo(() => {
    if (filtersOn) {
      const keep = new Set();
      matched.forEach((id) => { keep.add(id); (ancestry[id] || []).forEach((a) => keep.add(a)); });
      return rows.filter((c) => keep.has(c.cat_id))
        .map((c) => ({ ...c, _dim: !matched.has(c.cat_id) }));
    }
    return rows.filter((c) => !(ancestry[c.cat_id] || []).some((a) => collapsed.has(a)));
  }, [rows, filtersOn, matched, ancestry, collapsed]);

  const clearFilters = () => {
    setQ(""); setBranches([]); setVis("all"); setLevel("all"); setContent("all");
    setNarrowed("all"); setAuds([]); setTeams([]);
  };
  const toggleBranch = (id) => setCollapsed((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(Object.keys(childCount)));

  const descendants = (id) => {
    const set = new Set();
    const walk = (pid) => (cats || []).filter((c) => c.parent_id === pid).forEach((c) => { set.add(c.cat_id); walk(c.cat_id); });
    walk(id);
    return set;
  };

  const onDragOver = (e, c) => {
    if (filtersOn || !dragRef.current || dragRef.current === c.cat_id) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const mode = y < rect.height * 0.28 ? "before" : y > rect.height * 0.72 ? "after" : "inside";
    setOver({ id: c.cat_id, mode });
  };

  const onDrop = async (target) => {
    const dragCatId = dragRef.current;
    const mode = over?.mode || "before";
    setOver(null); setDragId(null); dragRef.current = null;
    if (filtersOn || !dragCatId || dragCatId === target.cat_id) return;
    const desc = descendants(dragCatId);
    const newParent = mode === "inside" ? target.cat_id : (target.parent_id || null);
    if (newParent && (newParent === dragCatId || desc.has(newParent))) {
      toast.error("Can't nest a category inside itself"); return;
    }
    const drag = cats.find((c) => c.cat_id === dragCatId);
    const sibs = cats.filter((c) => (c.parent_id || null) === (newParent || null) && c.cat_id !== dragCatId)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    let idx = sibs.length;
    if (mode !== "inside") {
      const ti = sibs.findIndex((c) => c.cat_id === target.cat_id);
      idx = ti < 0 ? sibs.length : (mode === "after" ? ti + 1 : ti);
    }
    sibs.splice(idx, 0, drag);
    const items = sibs.map((c, i) => ({ cat_id: c.cat_id, position: i + 1, parent_id: newParent || null }));
    try {
      await api.post("/kb/categories/reorder", { items });
      toast.success("Categories reordered");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const move = async (c, dir) => {
    const sibs = rows.filter((x) => (x.parent_id || null) === (c.parent_id || null));
    const i = sibs.findIndex((x) => x.cat_id === c.cat_id);
    const j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    const a = sibs[i], b = sibs[j];
    try {
      await api.post("/kb/categories/reorder", { items: [
        { cat_id: a.cat_id, position: b.position, parent_id: a.parent_id || null },
        { cat_id: b.cat_id, position: a.position, parent_id: b.parent_id || null },
      ] });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const archive = async (c) => {
    const name = c.name.trim();
    let preview = { articles: 0, subcategories: 0 };
    try {
      const { data } = await api.get(`/kb/categories/${c.slug}/archive-preview`);
      preview = data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    if (preview.articles > 0) {
      const yes = window.confirm(
        `“${name}” holds ${preview.articles} live article${preview.articles === 1 ? "" : "s"}`
        + `${preview.subcategories ? ` across ${preview.subcategories} sub-categorie(s)` : ""}.\n\n`
        + "Do you want to archive all articles in this category?\n\n"
        + "OK = archive the category and everything in it.\n"
        + "Cancel = leave everything alone so you can move the articles to another category first.");
      if (!yes) {
        toast.info("Nothing archived — move the articles to another category, then try again");
        return;
      }
    }
    try {
      const { data } = await api.post(`/kb/categories/${c.slug}/archive`,
        { archived: true, cascade: true });
      toast.success(`Archived “${name}” and ${data.articles} article${data.articles === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete “${c.name.trim()}”? This can't be undone.`)) return;
    try {
      await api.delete(`/kb/categories/${c.slug}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post("/kb/categories", {
        name: form.name.trim(),
        parent_id: form.parent_id === "all" ? null : form.parent_id,
        scope: form.scope,
      });
      toast.success("Category created");
      setForm({ name: "", parent_id: "all", scope: "external" });
      setCreating(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (denied) return <AppUnavailable appName="KB Categories" inactive={false} />;
  if (!cats) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>;

  return (
    <div className="kb-surface" data-testid="kb-manage-page">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/kb")} className="text-slate-400 dark:text-slate-500 hover:text-primary" data-testid="kb-manage-back"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Manage categories</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="kb-manage-count">
              {rows.length} categories — showing {shown.length}
            </p>
          </div>
        </div>
        <Button onClick={() => setCreating((v) => !v)} data-testid="kb-manage-new" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <FolderPlus className="h-4 w-4" /> New category
        </Button>
      </div>

      {creating && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid="kb-manage-create-form">
          <div className="flex-1 min-w-[200px]">
            <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Name</label>
            <Input value={form.name} data-testid="kb-manage-create-name" onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="w-[220px]">
            <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Parent</label>
            <KbCategoryFilter tree={cats} value={form.parent_id} single allLabel="— Top level —"
              testid="kb-manage-create-parent"
              onChange={(id) => setForm({ ...form, parent_id: id })} />
          </div>
          <div className="w-[200px]">
            <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Visibility</label>
            <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
              <SelectTrigger data-testid="kb-manage-create-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="external">External (everyone)</SelectItem>
                <SelectItem value="internal">Internal (staff only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={create} data-testid="kb-manage-create-save"><Plus className="h-4 w-4" /> Create</Button>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="kb-manage-cat-filters">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <Input data-testid="kb-manage-cat-search" value={q} placeholder="Search categories…"
            onChange={(e) => setQ(e.target.value)} className="h-9 pl-8" />
        </div>
        <div className="w-[190px]">
          <KbCategoryFilter tree={cats} value={branches} testid="kb-manage-cat-branch"
            onChange={setBranches} />
        </div>
        <Select value={vis} onValueChange={setVis}>
          <SelectTrigger className="w-[150px]" data-testid="kb-manage-cat-vis"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All visibility</SelectItem>
            <SelectItem value="external">Everyone</SelectItem>
            <SelectItem value="internal">Staff only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-[150px]" data-testid="kb-manage-cat-level"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="top">Top level only</SelectItem>
            <SelectItem value="sub">Sub-categories</SelectItem>
          </SelectContent>
        </Select>
        <Select value={content} onValueChange={setContent}>
          <SelectTrigger className="w-[150px]" data-testid="kb-manage-cat-content"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any content</SelectItem>
            <SelectItem value="empty">Empty only</SelectItem>
            <SelectItem value="has">Has articles</SelectItem>
          </SelectContent>
        </Select>
        <KbTagFilter options={KB_AUDIENCES.map((a) => ({ value: a.value, label: a.label }))}
          value={auds} onChange={setAuds} allLabel="All audiences"
          className="w-[160px]" testid="kb-manage-cat-auds" />
        <KbTagFilter options={teamOptions} value={teams} onChange={setTeams}
          allLabel="All teams" className="w-[150px]" testid="kb-manage-cat-teams" />
        <Select value={narrowed} onValueChange={setNarrowed}>
          <SelectTrigger className="w-[160px]" data-testid="kb-manage-cat-narrowed"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any targeting</SelectItem>
            <SelectItem value="only">Narrowed only</SelectItem>
          </SelectContent>
        </Select>
        {filtersOn ? (
          <button type="button" onClick={clearFilters} data-testid="kb-manage-cat-clear"
            className="text-xs font-medium text-primary hover:underline">Clear filters</button>
        ) : (
          <div className="flex items-center gap-2 text-xs">
            <button type="button" onClick={expandAll} data-testid="kb-manage-cat-expand"
              className="font-medium text-primary hover:underline">Expand all</button>
            <button type="button" onClick={collapseAll} data-testid="kb-manage-cat-collapse"
              className="font-medium text-primary hover:underline">Collapse all</button>
          </div>
        )}
      </div>

      {filtersOn && (
        <p className="mb-2 text-xs text-amber-600" data-testid="kb-manage-cat-filtered-note">
          Filtered view — reordering is off because this isn't the real order. Clear the filters to
          drag, or use Move… on any row.
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="kb-manage-list">
        {shown.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="kb-manage-cat-empty">
            No categories match those filters.
          </div>
        )}
        {shown.map((c) => {
          const kids = childCount[c.cat_id] || 0;
          const shut = collapsed.has(c.cat_id) && !filtersOn;
          return (
            <div key={c.cat_id} data-testid={`kb-manage-row-${c.slug}`}
              onDragOver={(e) => onDragOver(e, c)} onDrop={() => onDrop(c)}
              className={`flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-3 py-2.5 last:border-b-0 transition-colors
                ${dragId === c.cat_id ? "opacity-40" : "hover:bg-slate-50 dark:hover:bg-slate-800"}
                ${c._dim ? "opacity-60" : ""}
                ${over?.id === c.cat_id && over.mode === "inside" ? "bg-primary/5 ring-1 ring-inset ring-primary/40" : ""}
                ${over?.id === c.cat_id && over.mode === "before" ? "border-t-2 border-t-primary" : ""}
                ${over?.id === c.cat_id && over.mode === "after" ? "border-b-2 border-b-primary" : ""}`}>
              <span draggable={!filtersOn} data-testid={`kb-manage-grip-${c.slug}`}
                onDragStart={() => { if (filtersOn) return; dragRef.current = c.cat_id; setDragId(c.cat_id); }}
                onDragEnd={() => { dragRef.current = null; setDragId(null); setOver(null); }}
                title={filtersOn ? "Clear filters to reorder" : "Drag to reorder"}
                className={`shrink-0 ${filtersOn ? "cursor-not-allowed text-slate-200 dark:text-slate-700" : "cursor-grab text-slate-300 dark:text-slate-600 hover:text-slate-500 active:cursor-grabbing"}`}>
                <GripVertical className="h-4 w-4" />
              </span>
              <div className="flex min-w-0 flex-1 items-center gap-2" style={{ paddingLeft: `${c._depth * 20}px` }}>
                {kids > 0 ? (
                  <button type="button" onClick={() => toggleBranch(c.cat_id)} disabled={filtersOn}
                    data-testid={`kb-manage-toggle-${c.slug}`} title={`${kids} sub-categories`}
                    className="shrink-0 rounded text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 disabled:opacity-40">
                    {shut ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                ) : <span className="h-4 w-4 shrink-0" />}
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                  <KbIcon icon={c.icon} name={c.name} className="h-3.5 w-3.5" />
                </span>
                <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{c.name.trim()}</span>
                {shut && kids > 0 && (
                  <span className="shrink-0 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500 dark:text-slate-400">
                    +{kids}
                  </span>
                )}
                <span className="flex shrink-0 items-center gap-1" data-testid={`kb-manage-auds-${c.slug}`}>
                  {(c.audiences || []).slice(0, 3).map((a) => (
                    <span key={a} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${a === "everyone" ? "bg-emerald-100 text-emerald-700" : a === "internal" ? "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400" : "bg-sky-100 text-sky-700"}`}>
                      {audienceLabel(a)}
                    </span>
                  ))}
                  {(c.audiences || []).length > 3 && (
                    <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500 dark:text-slate-400">
                      +{(c.audiences || []).length - 3}
                    </span>
                  )}
                  {!(c.audiences || []).length && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Staff only</span>
                  )}
                </span>
                {((c.target_teams || []).length > 0 || (c.target_orgs || []).length > 0) && (
                  <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-indigo-600"
                    data-testid={`kb-manage-narrowed-${c.slug}`}
                    title="Narrowed — hidden in the reader from everyone outside this list">
                    <Lock className="h-3 w-3" />
                    {(c.target_teams || []).join(", ") || `${(c.target_orgs || []).length} orgs`}
                  </span>
                )}
                <span className="shrink-0 text-[11px] text-slate-400 dark:text-slate-500">{c.article_count_deep} articles</span>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <button onClick={() => move(c, -1)} disabled={filtersOn} data-testid={`kb-manage-up-${c.slug}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(c, 1)} disabled={filtersOn} data-testid={`kb-manage-down-${c.slug}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                <button onClick={() => setMoving(c)} data-testid={`kb-manage-move-${c.slug}`} title="Move — pick a new parent and position" className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary"><FolderInput className="h-4 w-4" /></button>
                <button onClick={() => setEditing(c)} data-testid={`kb-manage-edit-${c.slug}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => archive(c)} data-testid={`kb-manage-cat-archive-${c.slug}`} title="Archive — hides it from readers" className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-amber-50 hover:text-amber-600"><Archive className="h-4 w-4" /></button>
                <button onClick={() => del(c)} data-testid={`kb-manage-delete-${c.slug}`} className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <KbEditDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)} kind="category"
          record={editing} tree={cats} onSaved={() => { setEditing(null); load(); }} />
      )}
      <KbMoveCategoryDialog open={!!moving} onOpenChange={(v) => !v && setMoving(null)}
        category={moving} cats={cats} onMoved={load} />
    </div>
  );
}
